/**
 * QuoteCard — shared quote/plan card used on both Tilbud and Market pages.
 * Single source of truth for quote display with badges, pricing, features,
 * and category-specific detail accordions (Energy/Mobile/Internet).
 */
import { useState, useEffect, useRef } from 'react'
import { View, Text, Pressable, StyleSheet, Image } from 'react-native'
import Animated, { useAnimatedStyle, withTiming, FadeInDown } from 'react-native-reanimated'
import { Check, ChevronRight, Info, Star, TrendingDown } from 'lucide-react-native'
import EnergyBreakdownAccordion, {
  type TariffData,
  DEFAULT_KWH_YEAR,
  calcEnergyTotal,
  getEnergyBreakdown,
} from './EnergyBreakdown'
import { MobileDetailAccordion, InternetDetailAccordion } from './PlanDetailAccordions'
import PressableScale from './PressableScale'
import { colors, spacing, borderRadius, typography, shadows } from '../theme'
import { haptics } from '../utils/haptics'
import { getProviderLogo } from '../utils/providerLogos'

// ─── Types ───────────────────────────────────────

export interface QuoteData {
  id: string
  provider: string
  product_name?: string
  category: string
  monthly_price: number
  annual_price?: number
  creation_fee?: number
  details?: Record<string, any>
  status?: string
  created_at?: string
  estimated_annual_savings?: number
  valid_until?: string
  is_cheapest?: boolean
  is_best_value?: boolean
  is_recommended?: boolean
  recommendation_reason?: string
  effective_6m_total?: number
}

export interface Badge {
  label: string
  bg: string
  text: string
  hero?: boolean
  info?: string
}

export const BADGES: Record<string, Badge> = {
  cheapest:    { label: 'BILLIGSTE', bg: colors.successMuted, text: colors.successDark },
  best_value:  { label: 'BEDSTE VÆRDI', bg: colors.primaryLight, text: colors.primaryDark },
  best_6m:     { label: 'BEDST I 6 MND', bg: '#e0f2fe', text: '#0369a1' },
  runner_up:   { label: 'NÆST BILLIGST', bg: colors.surface, text: colors.text.secondary },
  partner:     { label: 'PARTNER', bg: '#fef3c7', text: '#92400e', hero: true },
}

// ─── Badge assignment helpers ────────────────────

export function getBadge(q: QuoteData): Badge | null {
  if (q.is_recommended && q.details?.partner_tier) return BADGES.partner
  if (q.is_cheapest) return BADGES.cheapest
  if (q.is_best_value) return BADGES.best_value
  return null
}

/** Calculate total cost over 6 months including intro pricing, creation fee, etc. */
export function calcEffective6mTotal(q: QuoteData): number {
  const monthly = q.monthly_price || 0
  const fee = q.creation_fee || q.details?.creation_fee || q.details?.creation_fee_kr || 0

  const introPrice = q.details?.intro_price
  const introPeriod = q.details?.intro_period
  const introOffer = q.details?.intro_offer

  // Structured: intro_price + intro_period
  if (introPrice != null && introPeriod) {
    const months = parseInt(String(introPeriod)) || 0
    if (months > 0) {
      const m = Math.min(months, 6)
      return (introPrice * m) + (monthly * Math.max(0, 6 - m)) + fee
    }
  }

  // Free-text: parse "39 kr/md i 2 mdr" etc.
  if (introOffer && typeof introOffer === 'string') {
    const krMatch = introOffer.match(/(\d+)\s*kr.*?(\d+)\s*(?:m[åa]n|md)/i)
    if (krMatch) {
      const ip = parseFloat(krMatch[1])
      const im = Math.min(parseInt(krMatch[2]), 6)
      return (ip * im) + (monthly * Math.max(0, 6 - im)) + fee
    }
    const gratisMatch = introOffer.match(/(\d+)\s*(?:m[åa]n|md).*?gratis/i)
    if (gratisMatch) {
      const im = Math.min(parseInt(gratisMatch[1]), 6)
      return (0 * im) + (monthly * Math.max(0, 6 - im)) + fee
    }
  }

  return monthly * 6 + fee
}

/** Smart best-value: best GB/kr for mobile, best Mbit/kr for internet, cheapest bagudbetalt for energy */
export function getBestValueIndex(quotes: QuoteData[], category: string, excludeIndices: Set<number>): number {
  if (quotes.length === 0) return -1
  if (category === 'mobile') {
    let bestIdx = -1, bestRatio = 0
    quotes.forEach((q, i) => {
      if (excludeIndices.has(i)) return
      // null gb_dk = unlimited — treat as very high GB for best-value calc
      const gb = q.details?.gb_dk == null ? 9999 : (Number(q.details?.gb_dk) || 0)
      const price = Number(q.monthly_price) || 999
      const ratio = gb / price
      if (ratio > bestRatio) { bestRatio = ratio; bestIdx = i }
    })
    return bestIdx
  }
  if (category === 'internet') {
    let bestIdx = -1, bestRatio = 0
    quotes.forEach((q, i) => {
      if (excludeIndices.has(i)) return
      const mbit = Number(q.details?.download_mbit) || 0
      const price = Number(q.monthly_price) || 999
      const ratio = mbit / price
      if (ratio > bestRatio) { bestRatio = ratio; bestIdx = i }
    })
    return bestIdx
  }
  // Energy: cheapest bagudbetalt (postpaid) plan
  let bestIdx = -1, lowestPrice = Infinity
  quotes.forEach((q, i) => {
    if (excludeIndices.has(i)) return
    const pt = (q.details?.payment_type || '').toLowerCase()
    const isBagud = pt.includes('bagud') || pt === 'postpaid'
    if (!isBagud) return
    const price = Number(q.monthly_price) || 999
    if (price < lowestPrice) { lowestPrice = price; bestIdx = i }
  })
  if (bestIdx === -1 && quotes.length > 0) {
    const pt0 = (quotes[0].details?.payment_type || '').toLowerCase()
    if (pt0.includes('bagud') || pt0 === 'postpaid') bestIdx = 0
  }
  return bestIdx
}

/**
 * Assign smart badges across ALL quotes for a category, then return the top 5 to display.
 * Badge winners are promoted into the displayed list even if they're not in the cheapest 5.
 *
 * @param allSortedQuotes - ALL non-accepted quotes sorted by monthly price (not just top 5)
 * @param category - energy, mobile, or internet
 * @returns { badges: Map<id, Badge>, displayQuotes: QuoteData[] } — displayQuotes is the 5 to render
 */
export function assignBadgesAndSelect(
  allSortedQuotes: QuoteData[],
  category: string,
): { badges: Map<string, Badge>; displayQuotes: QuoteData[] } {
  const badges = new Map<string, Badge>()
  if (allSortedQuotes.length === 0) return { badges, displayQuotes: [] }
  const assigned = new Set<string>()
  const promoted = new Set<string>() // IDs promoted from outside top 5

  // 1. BILLIGSTE — always index 0
  const cheapest = allSortedQuotes[0]
  if (cheapest) { badges.set(cheapest.id, BADGES.cheapest); assigned.add(cheapest.id) }

  // 2. BEDSTE VÆRDI — search ALL quotes (not just top 5)
  const bvIdx = getBestValueIndex(allSortedQuotes, category, new Set([0]))
  if (bvIdx >= 0) {
    const bv = allSortedQuotes[bvIdx]
    if (bv && !assigned.has(bv.id)) {
      badges.set(bv.id, {
        ...BADGES.best_value,
        info: category === 'energy'
          ? 'Billigste bagudbetalt-aftale — du betaler kun for reelt forbrug.'
          : 'Bedste forhold mellem indhold og pris.',
      })
      assigned.add(bv.id)
      if (bvIdx >= 5) promoted.add(bv.id)
    }
  }

  // 3. BEDST I 6 MND — cheapest 6-month total among unassigned quotes
  const best6m = allSortedQuotes
    .filter(q => !assigned.has(q.id))
    .map(q => ({ q, cost: calcEffective6mTotal(q) }))
    .sort((a, b) => a.cost - b.cost)[0]
  if (best6m) {
    badges.set(best6m.q.id, {
      ...BADGES.best_6m,
      info: 'Laveste totalpris over 6 måneder inkl. intro-rabat og oprettelse.',
    })
    assigned.add(best6m.q.id)
    const idx = allSortedQuotes.indexOf(best6m.q)
    if (idx >= 5) promoted.add(best6m.q.id)
  }

  // 4. NÆST BILLIGST — must be the actual 2nd cheapest (index 1), not just "next unassigned"
  if (allSortedQuotes.length > 1 && !assigned.has(allSortedQuotes[1].id)) {
    badges.set(allSortedQuotes[1].id, { ...BADGES.runner_up, info: 'Næstbilligste alternativ.' })
    assigned.add(allSortedQuotes[1].id)
  }

  // PARTNER slot disabled — will be re-enabled when partner program launches

  // Build display list: top 4 by price, but swap in promoted badge winners
  const top4ByPrice = allSortedQuotes.slice(0, 4)
  const promotedQuotes = allSortedQuotes.filter(q => promoted.has(q.id))

  // Replace the last N slots of top4 with promoted quotes (keeping order)
  const displayQuotes = [...top4ByPrice]
  for (const pq of promotedQuotes) {
    if (displayQuotes.some(q => q.id === pq.id)) continue // already in top 4
    // Replace the last non-badged quote in display list
    for (let i = displayQuotes.length - 1; i >= 0; i--) {
      if (!badges.has(displayQuotes[i].id)) {
        displayQuotes[i] = pq
        break
      }
    }
  }

  return { badges, displayQuotes }
}

/** Legacy wrapper — assigns badges to provided sorted quotes only (used by market page) */
export function assignBadges(sortedQuotes: QuoteData[], category: string): Map<string, Badge> {
  return assignBadgesAndSelect(sortedQuotes, category).badges
}

// ─── Feature extraction ──────────────────────────

export function getFeatures(q: QuoteData, category: string): string[] {
  const f: string[] = []
  const d = q.details || {}
  const isMobile = category === 'mobile'
  const isInternet = category === 'internet'

  if (d.green_energy) f.push('Grøn strøm')
  if (d.is_spot_based) f.push('Spotpris')

  if (d.contract_length != null) {
    const cl = String(d.contract_length).toLowerCase()
    const isNoBinding = d.contract_length === 0 || cl === '0' || cl === '0 mdr' || cl.includes('ingen binding')
    f.push(isNoBinding ? 'Ingen binding' : `Binding: ${d.contract_length}`)
  }

  if (isMobile) {
    if (d.gb_dk == null) f.push('Fri data')
    else if (d.gb_dk) f.push(`${d.gb_dk} GB data`)
    if (d.data_eu_gb) f.push(`${d.data_eu_gb} GB EU-data`)
    else if (d.eu_data) f.push('EU-data inkl.')
    if (d.roaming_terms && !String(d.roaming_terms).toLowerCase().includes('kun dk')) {
      const rt = String(d.roaming_terms).toLowerCase()
      if (rt.includes('verden') || rt.includes('world') || rt.includes('likehome')) {
        f.push('Verdensdata inkl.')
      }
    }
    if (d.calls_sms) f.push(d.calls_sms)
    if (d.network) f.push(`Netværk: ${d.network}`)
    if (d.hotspot_tethering === 'included' || d.hotspot_tethering === true) f.push('Hotspot inkl.')
  }

  if (isInternet) {
    if (d.download_mbit) {
      const upDown = d.upload_mbit ? `${d.download_mbit}/${d.upload_mbit}` : `${d.download_mbit}`
      f.push(`${upDown} Mbit/s`)
    }
    if (d.technology) f.push(d.technology)
    if (d.router_included) f.push('Router inkl.')
    if (d.coverage_area) f.push(d.coverage_area)
  }

  if (d.payment_type) {
    const pt = String(d.payment_type).toLowerCase()
    if (pt.includes('bagud') || pt === 'postpaid') f.push('Bagudbetalt')
    else if (pt.includes('forud') || pt === 'prepaid') f.push('Forudbetalt')
  }
  if (d.creation_fee != null && Number(d.creation_fee) > 0) f.push(`Oprettelse: ${Number(d.creation_fee)} kr`)
  const fee = Number(d.creation_fee ?? (q as any).creation_fee)
  if (fee === 0 && q.status !== 'public') f.push('Ingen oprettelse')

  return f
}

// ─── Provider color ──────────────────────────────

const PROVIDER_COLORS = [
  { bg: '#dbeafe', text: '#1d4ed8' },
  { bg: '#dcfce7', text: '#15803d' },
  { bg: '#f3e8ff', text: '#7c3aed' },
  { bg: '#fef3c7', text: '#b45309' },
  { bg: '#ffe4e6', text: '#be123c' },
  { bg: '#cffafe', text: '#0e7490' },
  { bg: '#fff7ed', text: '#ea580c' },
  { bg: '#f0fdfa', text: '#0d9488' },
]

export const getProviderColor = (p: string) => {
  const h = p.split('').reduce((a, c) => a + c.charCodeAt(0), 0)
  return PROVIDER_COLORS[h % PROVIDER_COLORS.length]
}

// ─── Price helpers ───────────────────────────────

export function getDisplayPrice(q: QuoteData, tariffs?: TariffData | null, yearlyKwh?: number): number {
  if (q.category === 'energy' && tariffs) {
    const bd = getEnergyBreakdown(q, tariffs, yearlyKwh)
    if (bd) return calcEnergyTotal(bd, tariffs, yearlyKwh || DEFAULT_KWH_YEAR)
  }
  const estimatedTotal = q.details?.estimated_total_monthly as number | undefined
  if (estimatedTotal && Math.round(estimatedTotal) !== Math.round(Number(q.monthly_price))) {
    return Math.round(estimatedTotal)
  }
  return Number(q.monthly_price)
}

export function getPricePrefix(q: QuoteData): string {
  return q.details?.is_spot_based ? '~' : ''
}

// ─── CATEGORY_CONFIG (shared) ────────────────────

export const CATEGORY_CONFIG: { id: string; label: string; emoji: string; color: string; bg: string }[] = [
  { id: 'energy', label: 'Strøm', emoji: '⚡', color: colors.categories.energy, bg: colors.categoriesBg.energy },
  { id: 'mobile', label: 'Mobil', emoji: '📱', color: colors.categories.mobile, bg: colors.categoriesBg.mobile },
  { id: 'internet', label: 'Internet', emoji: '🌐', color: colors.categories.internet, bg: colors.categoriesBg.internet },
]

// ─── QuoteCard ───────────────────────────────────

export interface QuoteCardProps {
  quote: QuoteData
  category: string
  /** Annual savings in kr. Overrides quote.estimated_annual_savings if provided. */
  savings?: number
  /** Tariff data for energy price recalculation */
  tariffs?: TariffData | null
  /** User's yearly kWh for energy calculations */
  yearlyKwh?: number
  /** Smart badge from assignBadges(). Pass null to show no badge, undefined to fall back to backend flags. */
  smartBadge?: Badge | null
  /** Accept handler */
  onAccept: () => void
}

function AnimatedChevron({ expanded }: { expanded: boolean }) {
  const animStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: withTiming(expanded ? '90deg' : '0deg', { duration: 200 }) }],
  }))
  return (
    <Animated.View style={animStyle}>
      <ChevronRight size={16} color={colors.text.muted} />
    </Animated.View>
  )
}

export default function QuoteCard({
  quote,
  category,
  savings,
  tariffs,
  yearlyKwh,
  smartBadge,
  onAccept,
}: QuoteCardProps) {
  const [expanded, setExpanded] = useState(false)
  const [showBadgeTip, setShowBadgeTip] = useState(false)
  const [showIntroTip, setShowIntroTip] = useState(false)
  const tipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Auto-dismiss tooltips after 3 seconds
  useEffect(() => {
    if (showBadgeTip || showIntroTip) {
      if (tipTimerRef.current) clearTimeout(tipTimerRef.current)
      tipTimerRef.current = setTimeout(() => { setShowBadgeTip(false); setShowIntroTip(false) }, 3000)
    }
    return () => { if (tipTimerRef.current) clearTimeout(tipTimerRef.current) }
  }, [showBadgeTip, showIntroTip])
  // Prefer smart badge; only fall back to backend flags when smart system has no entries
  const badge = smartBadge !== undefined ? smartBadge : getBadge(quote)
  const catConfig = CATEGORY_CONFIG.find(c => c.id === category)!
  const providerColor = getProviderColor(quote.provider)
  const providerLogo = getProviderLogo(quote.provider)
  const features = getFeatures(quote, category)
  const annualSavings = savings || Number(quote.estimated_annual_savings) || 0
  const monthlySavings = Math.round(annualSavings / 12)

  // Energy: recalculate display price using tariffs
  const isEnergy = category === 'energy'
  const breakdown = isEnergy ? getEnergyBreakdown(quote, tariffs ?? null, yearlyKwh) : undefined
  const displayPrice = getDisplayPrice(quote, tariffs, yearlyKwh)
  const pricePrefix = getPricePrefix(quote)

  const kwhSurcharge = quote.details?.kwh_surcharge as number | undefined
  const isVerified = !!quote.details?.partner_tier && quote.details.partner_tier === 'verified'
  const isIntroOffer = Boolean(
    quote.details?.intro_offer ||
    (Array.isArray(quote.details?.special_features) && quote.details.special_features.some((f: string) => f.toLowerCase().includes('introtilbud')))
  )
  const paymentType = quote.details?.payment_type as string | undefined

  // Build intro tooltip text from details
  const introOfferText = typeof quote.details?.intro_offer === 'string'
    ? quote.details.intro_offer
    : typeof quote.details?.intro_offer === 'object' && quote.details.intro_offer?.description
      ? quote.details.intro_offer.description
      : 'Prisen gælder kun i en introduktionsperiode og stiger herefter. Vi hjælper dig med at skifte når perioden udløber.'

  return (
    <PressableScale
      role="article"
      accessibilityLabel={`${quote.provider}${quote.product_name ? `, ${quote.product_name}` : ''}, ${Math.round(displayPrice).toLocaleString('da-DK')} kr/md${badge ? `, ${badge.label}` : ''}`}
      style={[
        s.card,
        badge?.hero && s.cardHero,
        badge?.label === 'BILLIGSTE' && s.cardCheapest,
      ]}
      onPress={() => { setExpanded(!expanded); setShowBadgeTip(false); setShowIntroTip(false) }}
      haptic="light"
    >
      {/* Header row */}
      <View style={s.header}>
        {providerLogo ? (
          <Image source={providerLogo} style={s.providerLogo} />
        ) : (
          <View style={[s.providerIcon, { backgroundColor: providerColor.bg }]}>
            <Text style={[s.providerInitial, { color: providerColor.text }]}>
              {quote.provider.charAt(0)}
            </Text>
          </View>
        )}
        <View style={{ flex: 1, minWidth: 0 }}>
          {/* Row 1: Provider name (full width) */}
          <Text style={s.providerName} numberOfLines={1}>{quote.provider}</Text>
          {/* Row 2: Primary badge */}
          {badge && (
            <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 3 }}>
              <View style={{ position: 'relative' }}>
                <Pressable
                  style={[s.badge, { backgroundColor: badge.bg }]}
                  onPress={badge.info ? (e) => { e.stopPropagation?.(); setShowBadgeTip(!showBadgeTip) } : undefined}
                >
                  {badge.hero && <Star size={10} color={colors.warningDark} />}
                  {badge.label === 'BILLIGSTE' && <TrendingDown size={10} color={badge.text} />}
                  {badge.label === 'BEDSTE VÆRDI' && <Star size={10} color={badge.text} />}
                  <Text style={[s.badgeText, { color: badge.text }]}>{badge.label}</Text>
                  {badge.info && <Info size={9} color={badge.text} style={{ opacity: 0.6 }} />}
                </Pressable>
                {showBadgeTip && badge.info && (
                  <Pressable style={s.tooltip} onPress={(e) => { e.stopPropagation?.(); setShowBadgeTip(false) }}>
                    <Text style={s.tooltipText}>{badge.info}</Text>
                  </Pressable>
                )}
              </View>
            </View>
          )}
          {/* Row 3: Product name */}
          {quote.product_name && (
            <Text style={[s.productName, { color: catConfig.color }]} numberOfLines={1}>
              {quote.product_name}
            </Text>
          )}
          {/* Row 4: Secondary badges (intro, verified, payment type) */}
          {(isIntroOffer || isVerified || (category === 'energy' && paymentType)) && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 3, flexWrap: 'wrap' }}>
              {isIntroOffer && (
                <View style={{ position: 'relative' }}>
                  <Pressable
                    style={s.introBadge}
                    onPress={(e) => { e.stopPropagation?.(); setShowIntroTip(!showIntroTip) }}
                  >
                    <Text style={s.introBadgeText}>Introtilbud</Text>
                    <Info size={9} color="#c2410c" style={{ opacity: 0.6 }} />
                  </Pressable>
                  {showIntroTip && (
                    <Pressable style={s.tooltip} onPress={(e) => { e.stopPropagation?.(); setShowIntroTip(false) }}>
                      <Text style={s.tooltipTitle}>Introduktionstilbud</Text>
                      <Text style={s.tooltipText}>{introOfferText}</Text>
                    </Pressable>
                  )}
                </View>
              )}
              {isVerified && (
                <View style={s.verifiedBadge}>
                  <Check size={10} color={colors.primaryDark} />
                  <Text style={s.verifiedBadgeText}>Verificeret</Text>
                </View>
              )}
              {category === 'energy' && paymentType && (() => {
                const pt = paymentType.toLowerCase()
                const isBagud = pt.includes('bagud') || pt === 'postpaid'
                const label = isBagud ? 'Bagudbetalt' : pt.includes('forud') || pt === 'prepaid' ? 'Forudbetalt' : null
                if (!label) return null
                return (
                  <View style={[s.paymentBadge, isBagud ? s.paymentBadgeBagud : s.paymentBadgeForud]}>
                    <Text style={[s.paymentBadgeText, isBagud ? { color: colors.successDark } : { color: colors.text.secondary }]}>{label}</Text>
                  </View>
                )
              })()}
            </View>
          )}
        </View>
        <View style={{ alignItems: 'flex-end', flexShrink: 0, minWidth: 100 }}>
          {quote.details?.intro_price != null && quote.details?.intro_period ? (
            <>
              <Text style={s.priceValue}>
                {Math.round(Number(quote.details.intro_price)).toLocaleString('da-DK')}
                <Text style={s.priceUnit}> kr/md</Text>
              </Text>
              <Text style={s.introAfterPrice}>
                Derefter {pricePrefix}{Math.round(displayPrice).toLocaleString('da-DK')} kr/md
              </Text>
            </>
          ) : (
            <Text style={s.priceValue}>
              {pricePrefix}{Math.round(displayPrice).toLocaleString('da-DK')}
              <Text style={s.priceUnit}> kr/md</Text>
            </Text>
          )}
          {monthlySavings > 0 && (
            <Text style={s.savingLine}>Spar {monthlySavings} kr/md</Text>
          )}
          {category === 'energy' && kwhSurcharge != null && (
            <Text style={s.kwhLine}>
              {Number(quote.details?.monthly_subscription ?? 0)} kr + {kwhSurcharge} øre/kWh
            </Text>
          )}
        </View>
        <AnimatedChevron expanded={expanded} />
      </View>

      {/* Expanded details */}
      {expanded && (
        <Animated.View entering={FadeInDown.duration(200)} style={s.expandedSection}>
          {annualSavings > 0 && (
            <View style={s.savingsBadge}>
              <Text style={s.savingsBadgeText}>
                Spar {Math.round(annualSavings).toLocaleString('da-DK')} kr/år
              </Text>
            </View>
          )}

          {/* Feature pills — only show for energy or when no detail accordion */}
          {features.length > 0 && category === 'energy' && (
            <View style={s.featureRow}>
              {features.slice(0, 5).map((f, i) => (
                <View key={i} style={s.featurePill}>
                  <Check size={10} color={colors.success} />
                  <Text style={s.featureText}>{f}</Text>
                </View>
              ))}
            </View>
          )}

          {/* Category-specific detail accordions */}
          {category === 'energy' && breakdown && (
            <View style={s.breakdownBox}>
              <EnergyBreakdownAccordion breakdown={breakdown} tariffs={tariffs ?? null} details={quote.details || {}} yearlyKwh={yearlyKwh} />
            </View>
          )}
          {category === 'mobile' && quote.details && (
            <MobileDetailAccordion details={quote.details} showSpecs />
          )}
          {category === 'internet' && quote.details && (
            <InternetDetailAccordion details={quote.details} showSpecs />
          )}

          {quote.recommendation_reason && (
            <View style={s.reasonBox}>
              <Text style={s.reasonText}>{quote.recommendation_reason}</Text>
            </View>
          )}

          <Pressable
            accessibilityRole="button"
            accessibilityLabel={`Vælg tilbud fra ${quote.provider}`}
            style={s.acceptButton}
            onPress={(e) => { e.stopPropagation(); haptics.medium(); onAccept() }}
          >
            <Text style={s.acceptButtonText}>
              {annualSavings > 0
                ? `Vælg tilbud — spar ~${Math.round(annualSavings).toLocaleString('da-DK')} kr/år`
                : 'Vælg tilbud'}
            </Text>
          </Pressable>
          <Text style={s.fortrydelsesret}>14 dages fortrydelsesret</Text>
        </Animated.View>
      )}
    </PressableScale>
  )
}

// ─── Styles ──────────────────────────────────────

const s = StyleSheet.create({
  card: {
    backgroundColor: colors.background, borderRadius: borderRadius.md,
    ...shadows.sm,
  },
  cardHero: { borderColor: colors.warning, borderWidth: 2 },
  cardCheapest: { borderColor: colors.success, borderWidth: 2 },
  header: {
    flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm, padding: spacing.md,
  },
  providerIcon: {
    width: 40, height: 40, borderRadius: 10, alignItems: 'center', justifyContent: 'center',
  },
  providerLogo: {
    width: 40, height: 40, borderRadius: 10,
  },
  providerInitial: { fontWeight: '700', fontSize: 18 },
  providerName: { ...typography.bodyBold, color: colors.text.primary, flexShrink: 1 },
  productName: { ...typography.small, marginTop: 2 },

  // Badges — consistent sizing: fontSize 10, fontWeight 700, borderRadius 4, padding 6h/2v
  badge: {
    flexDirection: 'row', alignItems: 'center', gap: 3,
    paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4,
  },
  badgeText: { fontSize: 10, fontWeight: '700', letterSpacing: 0.3 },
  introBadge: {
    flexDirection: 'row', alignItems: 'center', gap: 3,
    paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4,
    backgroundColor: '#fff7ed', borderWidth: 1, borderColor: '#fed7aa',
  },
  introBadgeText: { fontSize: 10, fontWeight: '700', color: '#c2410c', letterSpacing: 0.3 },
  verifiedBadge: {
    flexDirection: 'row', alignItems: 'center', gap: 2,
    paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4,
    backgroundColor: '#eff6ff',
  },
  verifiedBadgeText: { fontSize: 10, fontWeight: '500', color: colors.primaryDark, letterSpacing: 0.3 },
  paymentBadge: {
    paddingHorizontal: 6, paddingVertical: 1, borderRadius: 4,
    borderWidth: 1,
  },
  paymentBadgeBagud: { backgroundColor: '#ecfdf5', borderColor: '#a7f3d0' },
  paymentBadgeForud: { backgroundColor: '#f9fafb', borderColor: '#e5e7eb' },
  paymentBadgeText: { fontSize: 10, fontWeight: '500' },

  // Tooltip — positioned ABOVE badge to stay visible in scrollable lists
  tooltip: {
    position: 'absolute', left: 0, bottom: '100%', marginBottom: 6, zIndex: 50,
    width: 220, backgroundColor: '#ffffff', borderRadius: 10, padding: 10,
    borderWidth: 1, borderColor: colors.border,
    ...shadows.lg,
  },
  tooltipTitle: { fontSize: 11, fontWeight: '600', color: colors.text.primary, marginBottom: 4 },
  tooltipText: { fontSize: 10, color: colors.text.secondary, lineHeight: 15 },

  priceValue: { ...typography.h3, color: colors.text.primary },
  priceUnit: { ...typography.small, fontWeight: '400', color: colors.text.secondary },
  savingLine: { ...typography.small, color: colors.success, fontWeight: '500', marginTop: 1 },
  introAfterPrice: { ...typography.small, color: colors.text.muted, marginTop: 1 },
  kwhLine: { ...typography.small, color: colors.text.muted, marginTop: 1 },

  // Expanded
  expandedSection: {
    paddingHorizontal: spacing.md, paddingBottom: spacing.md,
    backgroundColor: '#fafafa', gap: spacing.sm,
    borderBottomLeftRadius: borderRadius.md,
    borderBottomRightRadius: borderRadius.md,
  },
  savingsBadge: {
    backgroundColor: '#f0fdf4', borderRadius: borderRadius.sm,
    paddingHorizontal: 8, paddingVertical: 6, alignSelf: 'flex-start',
  },
  savingsBadgeText: { ...typography.small, color: colors.successDark, fontWeight: '500' },
  featureRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  featurePill: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    backgroundColor: colors.background, paddingHorizontal: 8, paddingVertical: 4,
    borderRadius: 12, borderWidth: 1, borderColor: colors.borderLight,
  },
  featureText: { ...typography.small, color: colors.text.secondary },
  breakdownBox: {
    backgroundColor: colors.background, borderRadius: borderRadius.sm,
    padding: spacing.sm, borderWidth: 1, borderColor: colors.borderLight,
  },
  reasonBox: {
    backgroundColor: '#eff6ff', borderRadius: borderRadius.sm,
    paddingHorizontal: 8, paddingVertical: 6,
  },
  reasonText: { ...typography.small, color: colors.primary },
  acceptButton: {
    paddingVertical: 14, backgroundColor: colors.ctaGreen,
    borderRadius: borderRadius.md, alignItems: 'center',
    ...shadows.colored(colors.ctaGreen),
  },
  acceptButtonText: { ...typography.captionBold, color: colors.text.inverse, fontSize: 15 },
  fortrydelsesret: { ...typography.small, color: colors.text.muted, textAlign: 'center' },
})
